import type { PickerRow } from 'api/canonicalPicker.types'
import type {
  CanonicalLevel,
  PickerSliceState,
} from 'slices/aiSegmentBuilder/picker'
import {
  canonicalIdOf,
  picksOf,
  selectActiveSelection,
  selectActiveSource,
  selectBottomOperator,
  selectGroupLevel1Id,
  selectGroupLevel2Id,
  selectGroupLevel2OperatorOptions,
  selectGroupLevelConfig,
  selectGroupPickerId,
  selectGroupTopOperator,
  selectPickerIdsForGroup,
  SPECIFIC_SOURCE,
} from 'slices/aiSegmentBuilder/picker'

import toBackendOperator from 'slices/aiSegmentBuilder/picker/toBackendOperator'

/** Name → id, or name → every id, as thanx-ai reads selection_id_map and child_id_map */
export type IdMap = Record<string, string | string[]>

// thanx-ai reads any mode other than "specific" as smart
export type CanonicalMode = 'smart' | 'specific'

const modeOf = (source: string): CanonicalMode =>
  source === SPECIFIC_SOURCE ? 'specific' : 'smart'

export interface CanonicalParentSelection {
  /** Names the card; it can repeat across cards */
  parent_id: string
  /** Every level 1 value the card picked */
  parent_ids: string[]
  parent_mode: CanonicalMode
  operator: string
  mode: CanonicalMode
  child_ids?: string[]
  child_id_map?: IdMap
}

export interface CanonicalPickerSubmission {
  picker_id: string
  levels: [
    {
      id: string
      operator: string
      mode: CanonicalMode
      selection_ids: string[]
      selection_modes: Record<string, CanonicalMode>
      selection_id_map: IdMap
    },
    {
      id: string
      operator: string
      mode: CanonicalMode
      selection_ids: string[]
      selection_id_map: IdMap
      // Always sent, even empty: thanx-ai treats [] as no nested level, as it does an absent key
      parent_selections: CanonicalParentSelection[]
    }
  ]
}

const unique = (ids: string[]): string[] => Array.from(new Set(ids))

/**
 * A product targets its canonical id. A version targets its uid_group, which
 * thanx-dbt makes the group's global uid when it has one and its only store
 * uid when it has none. A pasted store uid, matched or not, targets itself.
 */
const targetIdOf = (row: PickerRow): string => {
  switch (row.kind) {
    case 'product':
      return row.id
    case 'uidGroup':
      return row.uidGroup
    case 'storeUid':
    case 'unmatchedUid':
      return row.uid
  }
}

const targetIdsOf = (rows: PickerRow[]): string[] =>
  unique(rows.map(targetIdOf))

/**
 * The name a picked row's ids are filed under. thanx-ai treats each name as
 * one requirement under "all", so every version of a canonical product shares
 * its product's name and any one of them satisfies it, as does a store uid of
 * one. A uid added without a match has no product, so it is filed under itself.
 */
export const nameMapEntryOf = (
  row: PickerRow
): { key: string; name: string } => {
  switch (row.kind) {
    case 'product':
      return { key: row.id, name: row.name }
    case 'uidGroup':
      return { key: row.canonicalId, name: row.canonicalName }
    case 'storeUid':
      return { key: row.canonicalId, name: row.name }
    case 'unmatchedUid':
      return { key: row.id, name: row.uid }
  }
}

/**
 * The id that names a card: its first pick's canonical product, or the first
 * uid added without a match when it picked nothing else.
 */
const cardIdOf = (picks: PickerRow[]): string =>
  picks.map(canonicalIdOf).find(id => id !== null) ?? targetIdOf(picks[0])

/** Two different products that share a name each keep their own entry */
export const idMapOf = (rows: PickerRow[]): IdMap => {
  const entries = new Map<string, { name: string; rows: PickerRow[] }>()
  rows.forEach(row => {
    const { key, name } = nameMapEntryOf(row)
    const entry = entries.get(key) ?? { name, rows: [] }
    entries.set(key, { name: entry.name, rows: [...entry.rows, row] })
  })

  const keysPerName = new Map<string, number>()
  entries.forEach(({ name }) =>
    keysPerName.set(name, (keysPerName.get(name) ?? 0) + 1)
  )

  return Object.fromEntries(
    Array.from(entries, ([key, { name, rows: entryRows }]) => {
      const ids = targetIdsOf(entryRows)
      const label = (keysPerName.get(name) ?? 0) > 1 ? `${name} (${key})` : name
      return [label, ids.length === 1 ? ids[0] : ids]
    })
  )
}

/** "specific" only when every pick sent is specific; thanx-ai reads it only for a card that names no mode */
const levelModeOf = (
  modes: CanonicalMode[],
  defaultSource: string
): CanonicalMode =>
  modes.length > 0 && modes.every(mode => mode === modes[0])
    ? modes[0]
    : modeOf(defaultSource)

/**
 * The picker response thanx-ai reads for a canonical picker. Each card is one
 * requirement: one parent_selection, in the order the cards show, whose
 * parent_ids hold every level 1 value it picked (any of them satisfies it)
 * under the card's own level 2. parent_id only names the card. A uid added
 * without a match is one more of those values, so it never narrows the card.
 */
export const buildCanonicalSubmission = (
  state: { picker: PickerSliceState },
  groupId: string
): CanonicalPickerSubmission => {
  const operatorOptions = selectGroupLevel2OperatorOptions(state, groupId)
  const defaultSourceOf = (level: CanonicalLevel) =>
    selectGroupLevelConfig(state, groupId, level).defaultSource ?? ''

  const selectionIds: string[] = []
  const selectionModes: Record<string, CanonicalMode> = {}
  const level1Picks: PickerRow[] = []
  const level2Picks: PickerRow[] = []
  const level1Modes: CanonicalMode[] = []
  const level2Modes: CanonicalMode[] = []
  const parentSelections: CanonicalParentSelection[] = []

  selectPickerIdsForGroup(state, groupId).forEach(pickerId => {
    const parentSource = selectActiveSource(state, pickerId, 'level1')
    const parents = picksOf(selectActiveSelection(state, pickerId, 'level1'))
    if (!parentSource || parents.length === 0) return

    const parentMode = modeOf(parentSource)
    const childMode = modeOf(
      selectActiveSource(state, pickerId, 'level2') ?? defaultSourceOf('level2')
    )
    const children = picksOf(selectActiveSelection(state, pickerId, 'level2'))
    const operator = toBackendOperator(
      selectBottomOperator(state, pickerId),
      children.length === 0
    )
    const sendsChildren =
      operatorOptions.find(option => option.id === operator)?.requiresValues !==
      false

    targetIdsOf(parents).forEach(id => {
      if (!(id in selectionModes)) selectionIds.push(id)
      selectionModes[id] = parentMode
    })
    level1Picks.push(...parents)
    level1Modes.push(parentMode)
    if (sendsChildren && children.length > 0) {
      level2Picks.push(...children)
      level2Modes.push(childMode)
    }

    parentSelections.push({
      parent_id: cardIdOf(parents),
      parent_ids: targetIdsOf(parents),
      parent_mode: parentMode,
      operator,
      mode: childMode,
      ...(sendsChildren && {
        child_ids: targetIdsOf(children),
        child_id_map: idMapOf(children),
      }),
    })
  })

  return {
    picker_id: selectGroupPickerId(state, groupId),
    levels: [
      {
        id: selectGroupLevel1Id(state, groupId),
        operator: selectGroupTopOperator(state, groupId),
        mode: levelModeOf(level1Modes, defaultSourceOf('level1')),
        selection_ids: selectionIds,
        selection_modes: selectionModes,
        selection_id_map: idMapOf(level1Picks),
      },
      {
        id: selectGroupLevel2Id(state, groupId),
        // Each card carries its own operator and children; thanx-ai reads this level's only as a card's fallback
        operator: operatorOptions[0]?.id ?? 'any',
        mode: levelModeOf(level2Modes, defaultSourceOf('level2')),
        selection_ids: [],
        selection_id_map: idMapOf(level2Picks),
        parent_selections: parentSelections,
      },
    ],
  }
}
